import { Injectable, inject } from '@angular/core';

import {
    CookieName,
    CookieService,
    DeviceService,
    FrontendHelperService,
    GoogleAnalyticsCoreService,
    Logger,
    MenuContentItem,
    NativeAppService,
    NavigationService,
    Page,
    SharedFeaturesApiService,
    TimerService,
    TrackingServiceProvider,
    TriggerEventPromiseResult,
    UserService,
    WINDOW,
    WebAnalyticsEventType,
    WebAnalyticsEvents,
    WebWorkerService,
    WindowEvent,
    WorkerType,
} from '@frontend/vanilla/core';
import { isObject, mapKeys, pickBy } from 'lodash-es';

import { TrackingValueGettersService } from './tracking-value-getters.service';
import { TrackingConfig } from './tracking.client-config';
import { CommunicationSettings, CommunicationType, ContentItemTracking, TrackingServiceOptions, eventNames } from './tracking.models';

/**
 * Service for tracking events and user interactions using a data layer approach.
 *
 * This service integrates with Google Tag Manager (GTM) to push tracking data
 * and events to the GTM data layer. It supports both initial value setup and
 * event tracking, including user-related values and error reporting.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class DataLayerTrackingService implements TrackingServiceProvider {
    private readonly apiService = inject(SharedFeaturesApiService);
    private readonly cookieService = inject(CookieService);
    private readonly deviceService = inject(DeviceService);
    private readonly frontendHelperService = inject(FrontendHelperService);
    private readonly googleAnalyticsCoreService = inject(GoogleAnalyticsCoreService);
    private readonly log = inject(Logger);
    private readonly nativeAppService = inject(NativeAppService);
    private readonly navigationService = inject(NavigationService);
    private readonly page = inject(Page);
    private readonly timerService = inject(TimerService);
    private readonly trackingConfig = inject(TrackingConfig);
    private readonly trackingValueGettersService = inject(TrackingValueGettersService);
    private readonly userService = inject(UserService);
    private readonly webWorkerService = inject(WebWorkerService);
    readonly #window = inject(WINDOW);

    storedTrackingData: any[] = [];

    constructor() {
        // Create interval to update data layer when GTM script loads.
        this.webWorkerService.createWorker(WorkerType.DataLayerTrackingInterval, { interval: 500 }, async () => {
            if (!this.gtmLoaded || (!this.googleAnalyticsCoreService.isReady && this.trackingConfig.useMeasurementProtocol)) {
                return;
            }

            this.googleAnalyticsCoreService.updateCookies();
            this.processStoredData();
            this.webWorkerService.removeWorker(WorkerType.DataLayerTrackingInterval);
        });
    }

    private get gtmEnabled(): boolean {
        return this.trackingConfig.tagManagerRenderers.includes('GoogleTagManagerRenderer') && !!this.#window.google_tag_manager;
    }

    private get gtmLoaded(): boolean {
        return (
            this.#window.dataLayer?.some((x: { event: string }) => x.event === WindowEvent.VanillaGtmLoaded) &&
            this.#window.dataLayer?.some((x: { event: string }) => x.event === WindowEvent.GtmLoad)
        );
    }

    /**
     * Adds initial tracking values to the data layer.
     *
     * This method performs the following steps:
     * 1. Updates the data layer with entry information (such as entry URL and UTM parameters).
     * 2. Updates user-related values asynchronously.
     * 3. Pushes various environment, device, page, and user properties to the data layer,
     *    including user agent, device specs, language, night mode status, and more.
     * 4. Updates the data layer with the current session ID if available.
     *
     * This is typically called at the start of a session or page load to ensure
     * that all relevant tracking information is available for analytics.
     */
    async addInitialValues() {
        this.updateDataLayerEntryInfo();
        await this.updateUserValues();
        this.updateDataLayer({
            'browser.userAgent': this.#window.navigator.userAgent,
            'browser.isTouch': this.deviceService.isTouch,
            'page.trackerID': this.cookieService.get(CookieName.TrackerId),
            'page.redirectedFrom': this.cookieService.get(CookieName.RedirexOriginal),
            'page.nightModeStatus': this.trackingValueGettersService.nightMode(),
            'page.language': this.page.languageCode,
            'page.culture': this.page.lang,
            'page.frontend': this.frontendHelperService.getFrontendDescription(),
            'page.domain': this.page.domain,
            'page.medium': this.trackingValueGettersService.medium(),
            'native.nativeMode': this.nativeAppService.nativeMode,
            'user.profile.dvID': this.cookieService.get(CookieName.DeviceId),
            'user.session.internalCampaign': this.navigationService.location.search.get('trid'),
            'device.cpuCores': this.deviceService.cpuCores,
            'device.cpuMaxFrequency': this.deviceService.cpuMaxFrequency,
            'device.totalRam': this.deviceService.totalRam,
            'device.model': this.deviceService.model,
            'page.trackingAffiliate': this.cookieService.get(CookieName.TrackingAffiliate),
            'browser.orientation': this.deviceService.currentOrientation,
            'browser.screenResolution': this.trackingValueGettersService.browserScreenResolution(),
        });

        this.updateDataLayerSessionId();
    }

    /**
     * Updates user-related values in the data layer.
     *
     * This method collects various user profile attributes such as account ID, balance,
     * currency, country, loyalty status, and more. It also checks if the user has a positive
     * balance and whether they are authenticated or existing.
     *
     * The collected data is then pushed to the data layer for tracking purposes.
     */
    async updateUserValues() {
        this.updateDataLayer({
            'user.profile.accountID': this.userService.accountId,
            'user.profile.bal': await this.trackingValueGettersService.userBalance(),
            'user.profile.currency': this.userService.currency,
            'user.profile.country': this.userService.country,
            'user.profile.loyaltyStatus': this.userService.loyalty ?? '',
            'user.profile.opid': this.userService.customerId,
            'user.profile.vid': this.userService.segmentId,
            'user.profile.stage': this.userService.lifeCycleStage ?? '',
            'user.profile.prestage': this.userService.eWarningVip ?? '',
            'user.profile.mid': this.userService.microSegmentId,
            'user.profile.chid': this.userService.churnRate,
            'user.profile.fvid': this.userService.futureValue,
            'user.profile.pvid': this.userService.potentialVip,
            'user.session.geoIPCountry': this.userService.geoCountry,
            'user.session.abTestGroup': this.cookieService.get(CookieName.MmcoreBwinvar),
            'user.hasPositiveBalance': await this.trackingValueGettersService.hasPositiveBalance(),
            'user.isAuthenticated': this.userService.isAuthenticated,
            'user.isExisting': !!this.cookieService.get(CookieName.LastVisitor),
            'browser.orientation': this.deviceService.currentOrientation,
            'browser.screenResolution': this.trackingValueGettersService.browserScreenResolution(),
        });
    }

    /**
     * Updates the data layer with the provided data.
     *
     * If the data is an object and GTM is loaded, it pushes the data to the GTM data layer.
     * If GTM is not loaded, it stores the data in local storage for later processing.
     * If the scheduler is enabled and a timeout is specified, it uses an idle callback to push the data after a delay.
     *
     * @param data The data to be pushed to the data layer.
     * @returns A promise that resolves when the data has been pushed or stored.
     */
    async updateDataLayer(data?: any): Promise<void> {
        if (this.trackingConfig.useMeasurementProtocol) {
            return new Promise((resolve) => {
                const name = data?.event ?? 'pageView';
                delete data?.event;

                this.googleAnalyticsCoreService.track({ name, params: data });
                resolve();
            });
        }

        return this.pushToDataLayer(data);
    }

    /**
     * Tracks web analytics events for a given menu content item and event type.
     *
     * Parses the `webAnalytics` property of the provided `item` and triggers the corresponding
     * analytics event based on the specified `eventType` (load, click, or close).
     * For click events, if the `component.URLClicked` is set to 'link', it replaces it with the item's URL.
     * Logs a remote error if parsing or tracking fails.
     *
     * @param item The menu content item containing web analytics data.
     * @param eventType The type of web analytics event to track.
     */
    async trackEvents(item: MenuContentItem, eventType: WebAnalyticsEventType) {
        if (item.webAnalytics) {
            try {
                const parsedValue: WebAnalyticsEvents = JSON.parse(item.webAnalytics);

                switch (eventType) {
                    case WebAnalyticsEventType.load:
                        if (parsedValue?.load) {
                            await this.triggerEvent(parsedValue.load.eventName, parsedValue.load.data);
                        }
                        break;
                    case WebAnalyticsEventType.click:
                        if (parsedValue?.click) {
                            if (parsedValue.click.data['component.URLClicked'] === 'link') {
                                parsedValue.click.data['component.URLClicked'] = item.url;
                            }
                            await this.triggerEvent(parsedValue.click.eventName, parsedValue.click.data);
                        }
                        break;
                    case WebAnalyticsEventType.close:
                        if (parsedValue?.close) {
                            await this.triggerEvent(parsedValue.close.eventName, parsedValue.close.data);
                        }
                        break;
                }
            } catch (error) {
                this.log.errorRemote(
                    `Failed to parse or track WebAnalytics data from item:${item.name}. Provided WebAnalytics data:${item.webAnalytics}`,
                    error,
                );
            }
        }
    }

    /**
     * Triggers a tracking event by pushing data to the data layer and handling event callbacks.
     *
     * @param eventName The name of the event to trigger.
     * @param data Optional data to include with the event.
     * @param options Optional tracking service options, such as timeout.
     * @returns A promise that resolves with the result of the trigger event.
     */
    async triggerEvent(eventName: string, data?: any, options?: TrackingServiceOptions): Promise<TriggerEventPromiseResult> {
        // GTM must be enabled, not only loaded to make sure it's injected into a page via Vanilla's data layer tracking implementation.
        const eventTimeout = this.gtmEnabled ? (options?.timeout ?? this.trackingConfig.eventCallbackTimeoutInMilliseconds) : 0;

        return this.runWithTimeout(
            new Promise((resolve, reject) => {
                if (eventName.toString().startsWith('error.')) {
                    // "error.*" eventName is reserved for errors reporting
                    return reject('The "error.*" eventName is reserved for errors reporting');
                }

                if (!data || Object.keys(data).length === 0) {
                    return reject('Tracking data is required');
                }

                if (eventName === eventNames.userLogout) {
                    this.updateUserValues();
                }

                data = {
                    event: eventName,
                    ...data,
                };

                if (this.gtmEnabled) {
                    data = {
                        eventCallback: resolve.bind(null, TriggerEventPromiseResult.Normal),
                        eventTimeout,
                        ...data,
                    };
                }

                this.updateDataLayer(data);

                // Tracking cleanup
                this.cookieService.remove(CookieName.RedirexOriginal);

                // Resolve immediately when GTM is not present (other tag managers don't support event callback)
                if (!this.gtmEnabled) {
                    resolve(TriggerEventPromiseResult.Normal);
                }
            }),
            eventTimeout,
        );
    }

    /**
     * Reports an error object to the data layer.
     *
     * @param error The error object to report.
     * @param options Optional parameters, such as whether to track a custom timestamp.
     * @returns A promise that resolves when the data layer has been updated.
     */
    async reportErrorObject(error: any, options?: any) {
        const data: any = {
            event: 'error',
            error,
        };

        if (options?.trackCustomTimeStamp) {
            data['login.customTimeStamp'] = Date.now() * 1000;
        }

        return this.updateDataLayer(data);
    }

    /**
     * Reports an error detail to the data layer.
     *
     * @param errorDetail The details of the error to report.
     */
    reportError(errorDetail: any) {
        this.updateDataLayer({
            event: encodeURIComponent(`error.${JSON.stringify(errorDetail)}`),
        });
    }

    /**
     * Tracks a content item event by extracting the event and data from the provided parameters.
     *
     * @param parameters The parameters containing tracking information.
     * @param parameterName The name of the parameter to extract tracking information from.
     */
    trackContentItemEvent(parameters: { [name: string]: string } | undefined, parameterName: string) {
        const tracking = this.getContentItemTracking(parameters, parameterName);

        if (tracking) {
            this.triggerEvent(tracking.event, tracking.data);
        }
    }

    /**
     * Sets the referrer for tracking purposes.
     *
     * @param referrer The referrer URL to set.
     */
    setReferrer(referrer: string) {
        this.trackingValueGettersService.setReferrer(referrer);
    }

    /**
     * Gets user communication settings and trigger the tracking event `Event.Functionality.Cts`
     * with data formatted as follows: **Email:Yes|Post:No|SMS:Yes|Phone:No**
     */
    updateUserContactabilityStatus() {
        this.apiService.get('communicationsettings').subscribe((data: CommunicationSettings) => {
            const cts = data.settings.map((c: CommunicationType) => `${c.name}:${c.selected ? 'Yes' : 'No'}`).join('|');
            this.triggerEvent('Event.Functionality.Cts', { 'user.profile.cts': cts });
        });
    }

    /**
     * Retrieves content item tracking information from the provided parameters.
     *
     * @param parameters The parameters object containing tracking information.
     * @param parameterName The name of the parameter to look for.
     * @returns A ContentItemTracking object or null if the parameter is not found.
     */
    getContentItemTracking(
        parameters:
            | {
                  [name: string]: string;
              }
            | undefined,
        parameterName: string,
    ): ContentItemTracking | null {
        parameters = parameters || {};
        const event = parameters[parameterName];

        if (!event) {
            return null;
        }

        const trackingDataPrefix = parameterName + '.';
        const data = mapKeys(
            pickBy(parameters, (_: string, k: string) => k.startsWith(trackingDataPrefix)),
            (_: string, k: string) => k.replace(trackingDataPrefix, ''),
        );

        return { event, data };
    }

    private pushToDataLayer(data?: any): Promise<void> {
        return new Promise((resolve) => {
            if (isObject(data) && !Array.isArray(data)) {
                if (this.gtmLoaded) {
                    if (!this.trackingConfig.schedulerEnabled && this.trackingConfig.dataLayerUpdateTimeoutInMilliseconds) {
                        this.timerService.scheduleIdleCallback(() => {
                            this.#window[this.trackingConfig.dataLayerName].push(data);
                            resolve();
                        }, this.trackingConfig.dataLayerUpdateTimeoutInMilliseconds);
                    } else {
                        this.#window[this.trackingConfig.dataLayerName].push(data);
                        resolve();
                    }
                } else {
                    this.storedTrackingData.push(data);
                    resolve();
                }
            }
        });
    }

    private processStoredData() {
        if (this.storedTrackingData.length > 0) {
            this.storedTrackingData.forEach((data: any) => this.updateDataLayer(data));
            this.storedTrackingData = [];
        }
    }

    private runWithTimeout(promise: Promise<TriggerEventPromiseResult>, timeout: number): Promise<TriggerEventPromiseResult> {
        return new Promise((resolve, reject) => {
            const timeoutId = this.timerService.scheduleIdleCallback(() => resolve(TriggerEventPromiseResult.Timeout), timeout);

            promise.then(
                (result: TriggerEventPromiseResult) => {
                    timeoutId();
                    resolve(result);
                },
                (error: any) => {
                    timeoutId();
                    reject(error);
                },
            );
        });
    }

    private updateDataLayerEntryInfo() {
        const entryUrl = this.cookieService.get(CookieName.EntryUrl);

        if (entryUrl) {
            this.trackingValueGettersService.setEntryUrl(entryUrl);
            this.updateDataLayer({
                event: 'v8Launch',
                v8: {
                    tech: {
                        build: this.#window?.VERSION,
                        label: this.page.domain,
                    },
                    entry: {
                        url: this.trackingValueGettersService.fullEntryUrl().absUrl(),
                        path: this.trackingValueGettersService.fullEntryUrl().path(),
                        referrer: this.cookieService.get(CookieName.EntryUrlReferrer),
                        utms: {
                            source: this.trackingValueGettersService.fullEntryUrl().search.get('utm_source'),
                            medium: this.trackingValueGettersService.fullEntryUrl().search.get('utm_medium'),
                            campaign: this.trackingValueGettersService.fullEntryUrl().search.get('utm_campaign'),
                            content: this.trackingValueGettersService.fullEntryUrl().search.get('utm_content'),
                            keyword: this.trackingValueGettersService.fullEntryUrl().search.get('utm_term'),
                        },
                    },
                },
            });
        }
    }

    private updateDataLayerSessionId() {
        if (this.#window.hasOwnProperty('decibelInsight')) {
            this.#window.decibelInsight('ready', () => {
                this.updateDataLayer({ 'user.decibelID': this.#window.decibelInsight('getSessionId') });
            });
        } else {
            this.#window._da_ready = () => {
                this.updateDataLayer({ 'user.decibelID': this.#window.decibelInsight('getSessionId') });
            };
        }
    }
}
